import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, ChevronsUpDown, Plus, Search } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';

// Shared "select or create" dropdown backed by the unified category+supplier list.
// Self-contained (no nested Popover) so search & typing work even inside Drawers.
export function TagCombobox({ value, onChange, placeholder = 'Select…', testId, allowCreate = true, onCreated, className = '', createExtra = {}, filterKind = null }) {
  const qc = useQueryClient();
  const wrapRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const { data } = useQuery({ queryKey: ['catalog-tags'], queryFn: async () => (await api.get('/catalog/tags')).data.data });
  const tags = data || [];
  const pool = filterKind === 'category' ? tags.filter((t) => t.is_category)
    : filterKind === 'supplier' ? tags.filter((t) => t.is_supplier)
    : tags;
  const q = query.trim();
  const filtered = q ? pool.filter((t) => t.name.toLowerCase().includes(q.toLowerCase())) : pool;
  const exact = tags.some((t) => t.name.toLowerCase() === q.toLowerCase());

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (name) => { onChange(name); setOpen(false); setQuery(''); };

  const create = async () => {
    if (!q) return;
    try {
      const r = await api.post('/catalog/tags', { name: q, ...createExtra });
      qc.invalidateQueries({ queryKey: ['catalog-tags'] });
      qc.invalidateQueries({ queryKey: ['suppliers'] });
      if (onCreated) onCreated(r.data.data);
      pick(r.data.data.name);
      toast.success(`"${r.data.data.name}" added`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="relative" ref={wrapRef}>
      <Button type="button" variant="outline" role="combobox" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className={`h-11 w-full justify-between rounded-xl font-normal ${!value ? 'text-muted-foreground' : ''} ${className}`}>
        <span className="truncate">{value || placeholder}</span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
      {open && (
        <div className="absolute left-0 right-0 z-[400] mt-1 overflow-hidden rounded-xl border border-border bg-popover shadow-lg" data-testid={testId ? `${testId}-panel` : undefined}>
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search or type a name…"
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              data-testid={testId ? `${testId}-input` : undefined}
              onKeyDown={(e) => { if (e.key === 'Enter' && allowCreate && q && !exact) { e.preventDefault(); create(); } }} />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {filtered.map((t) => (
              <button key={t.name} type="button" onClick={() => pick(t.name)} data-testid={testId ? `${testId}-opt` : undefined}
                className="flex w-full items-center px-3 py-2 text-left text-sm hover:bg-secondary">
                <Check className={`mr-2 h-4 w-4 shrink-0 ${value === t.name ? 'opacity-100 text-primary' : 'opacity-0'}`} />
                <span className="truncate">{t.name}</span>
              </button>
            ))}
            {allowCreate && q && !exact && (
              <button type="button" onClick={create} data-testid={testId ? `${testId}-create` : undefined}
                className="flex w-full items-center px-3 py-2 text-left text-sm font-medium text-primary hover:bg-secondary">
                <Plus className="mr-2 h-4 w-4 shrink-0" /> Create &quot;{q}&quot;
              </button>
            )}
            {filtered.length === 0 && !(allowCreate && q) && (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">No matches.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
